import { ReactNode } from 'react';
import { Box, Center, Heading, Tag, TagLabel, Text } from '@chakra-ui/react';
import { Meta, OpenGraphType } from 'components/HeadContent';
import { getLayout } from 'layouts/TocLayout';

const LIST_LEFT_MARGIN_MULTIPLIER = 10;

export interface ChildrenOnly {
  children: ReactNode;
}

export const ChapterHeading = ({ children }: ChildrenOnly) => (
  <Heading size="lg" as="h2" color="brand.red" mb={5}>
    {children}
  </Heading>
);

export const ChapterContents = ({ children }: ChildrenOnly) => (
  <Box ml={LIST_LEFT_MARGIN_MULTIPLIER * 1} mb={5}>
    {children}
  </Box>
);

export const SectionLabel = ({ children }: ChildrenOnly) => (
  <Text as="h3">{children}</Text>
);

export const ComingSoon = ({ children }: ChildrenOnly) => (
  <Box opacity={0.5}>{children}</Box>
);

export const ComingSoonTag = () => (
  <Tag size="lg" variant="subtle" colorScheme="red" mb={2}>
    <TagLabel
      fontWeight="bold"
      color="brand.red"
      backgroundColor="brand.fadedRed"
    >
      COMING SOON
    </TagLabel>
  </Tag>
);

export const NewTag = () => (
  <Tag size="lg" variant="subtle" mb={2} colorScheme="green">
    <TagLabel fontWeight="bold">🚀 JUST ADDED! 🚀</TagLabel>
  </Tag>
);

export function TOC() {
  return (
    <>
      <Meta
        relativeUrl="/toc"
        titlePrefix="Table of Contents"
        description="Full table of contents for the book, Cut Into The Jamstack: Build and Deploy a Full-Stack Application with React and Next.js."
        type={OpenGraphType.ARTICLE}
        images={[
          {
            url: `${process.env.SITE_BASE_URL}/cutintothejamstack-logo-inline.png`,
            width: 456,
            height: 100,
            alt: 'Cut Into The Jamstack Logo',
          },
        ]}
      />

      <Center mb={10}>
        <Heading size="xl" as="h1">
          Table of Contents
        </Heading>
      </Center>

      <ChapterHeading>About this Beta</ChapterHeading>

      <ChapterHeading>The What and Why</ChapterHeading>
      <ChapterContents>
        <SectionLabel>What you’ll learn in this book</SectionLabel>
        <SectionLabel>Who this book is for</SectionLabel>
        <SectionLabel>
          What you should know before reading this book
        </SectionLabel>
        <SectionLabel>Tools and libraries we chose and why</SectionLabel>
        <SectionLabel>Conventions used in this book</SectionLabel>
        <SectionLabel>The app we’ll be building</SectionLabel>
      </ChapterContents>

      <ChapterHeading>Laying Our Foundation</ChapterHeading>
      <ChapterContents>
        <SectionLabel>System Prerequisites</SectionLabel>
        <SectionLabel>Setting up our environment</SectionLabel>
        <SectionLabel>Folder Structure</SectionLabel>
        <SectionLabel>Setting up Chakra-UI</SectionLabel>
        <SectionLabel>Configuring Prisma</SectionLabel>
        {/* <SectionLabel>Building a Beautiful Homepage</SectionLabel>
        <SectionLabel>Adding Terms and Privacy Pages</SectionLabel> */}
      </ChapterContents>

      <ChapterHeading>Building a Full-Stack Screen</ChapterHeading>
      <ChapterContents>
        <SectionLabel>How we’ll build our first screen: steps</SectionLabel>
        <SectionLabel>Creating our first database table</SectionLabel>
        <SectionLabel>Seeding the database</SectionLabel>
        <SectionLabel>Creating our dashboard API endpoint</SectionLabel>
        <SectionLabel>Starting our dashboard page</SectionLabel>
        <SectionLabel>Adding some visual elements with Chakra-UI</SectionLabel>
      </ChapterContents>

      <ChapterHeading>Shipping to Production</ChapterHeading>
      <ChapterContents>
        <SectionLabel>Ship, Ship, Ship - Going to Production</SectionLabel>
        <SectionLabel>Our hosting providers</SectionLabel>
        <SectionLabel>How deployment will work</SectionLabel>
        <SectionLabel>Configuring our setup for deployment</SectionLabel>
        <SectionLabel>Push the red button: let’s deploy! 🚀</SectionLabel>
        <SectionLabel>Git branch strategy</SectionLabel>
      </ChapterContents>

      <ChapterHeading>Full-Stack Gallery CRUD</ChapterHeading>
      <ChapterContents>
        <SectionLabel>Gallery CRUD</SectionLabel>
        <SectionLabel>Planning Gallery API endpoints</SectionLabel>
        <SectionLabel>Structure of Our CRUD Endpoints</SectionLabel>
        <SectionLabel>Gallery Creation API Endpoint</SectionLabel>
        <SectionLabel>Gallery Retrieval API Endpoint</SectionLabel>
        <SectionLabel>Gallery Update API Endpoint</SectionLabel>
        <SectionLabel>Gallery Deletion API Endpoint</SectionLabel>
        <SectionLabel>Planning Gallery CRUD UI</SectionLabel>
        <SectionLabel>The GalleryListItem Component</SectionLabel>
        <SectionLabel>The GalleryCreateModal Component</SectionLabel>
        <SectionLabel>The GalleryEditModal Component</SectionLabel>
        <SectionLabel>The GalleryDeleteDialog Component</SectionLabel>
        <SectionLabel>Creating an API Layer</SectionLabel>
        <SectionLabel>Creating a Reusable Error Component</SectionLabel>
        <SectionLabel>The Connected Dashboard</SectionLabel>
        <SectionLabel>
          Connecting the GalleryCreateModal and ErrorAlert
        </SectionLabel>
        <SectionLabel>Connecting the GalleryEditModal</SectionLabel>
        <SectionLabel>Enabling Gallery Deletion</SectionLabel>
      </ChapterContents>

      <NewTag />
      <ChapterHeading>Photo Gallery and Image Uploads</ChapterHeading>
      <ChapterContents>
        <SectionLabel>Image Architecture Planning</SectionLabel>
        <SectionLabel>Configuring Cloudinary</SectionLabel>
        <SectionLabel>Important Cloudinary Links</SectionLabel>
        <SectionLabel>Configuring Cloudinary</SectionLabel>
        <SectionLabel>Adding Image Support to Our Database</SectionLabel>
        <SectionLabel>Building the Photo Gallery Page</SectionLabel>
        <SectionLabel>DRYing up API Routes With Custom Middleware</SectionLabel>
        <SectionLabel>Building Photo API Endpoints</SectionLabel>
        <SectionLabel>Adding the Front-End API Layer</SectionLabel>
        <SectionLabel>Transforming iOS-Specific File Formats</SectionLabel>
        <SectionLabel>
          Adding Single-File Image Uploads to the Gallery Editor
        </SectionLabel>
        <SectionLabel>Adding Photo Deletion to the Gallery Editor</SectionLabel>
        {/* <SectionLabel>Adding Async Multi-File Upload Support</SectionLabel> */}
      </ChapterContents>

      <ComingSoonTag />
      <ComingSoon>
        <ChapterHeading>Photo Gallery Sharing</ChapterHeading>
        <ChapterContents>
          <SectionLabel>Adding Public Gallery Routes</SectionLabel>
          <SectionLabel>Planning for UI Reuse</SectionLabel>
          <SectionLabel>
            Separating Gallery Editor and Public Gallery
          </SectionLabel>
          <SectionLabel>Adding Gallery Sharing</SectionLabel>
        </ChapterContents>

        <ChapterHeading>Authentication and Access Control</ChapterHeading>
        <ChapterContents>
          <SectionLabel>
            Overview: Authentication and Access Control
          </SectionLabel>
          <SectionLabel>
            Configuring our hosting providers for authentication
          </SectionLabel>
          <SectionLabel>How authentication will work</SectionLabel>
          <SectionLabel>How access control will work</SectionLabel>
          <SectionLabel>About next-auth</SectionLabel>
          <SectionLabel>
            Creating &amp; Configuring OAuth Provider Accounts
          </SectionLabel>
          <SectionLabel>Installing and Configuring next-auth</SectionLabel>
          <SectionLabel>Requiring authentication with our screens</SectionLabel>
          <SectionLabel>
            Requiring authentication in our API endpoints
          </SectionLabel>
          <SectionLabel>
            Restricting users to resources they control
          </SectionLabel>
          <SectionLabel>
            Changing navbar state based on whether user is logged in
          </SectionLabel>
        </ChapterContents>

        <ChapterHeading>$$$: Payment Processing w/Stripe</ChapterHeading>
        <ChapterContents>
          <SectionLabel>Loading Stripe Dependencies</SectionLabel>
          <SectionLabel>Paid / Unpaid User Access</SectionLabel>
          <SectionLabel>How Checkout Flow Will Work</SectionLabel>
          <SectionLabel>
            Creating &amp; Configuring a Stripe Account For Subscriptions
          </SectionLabel>
          <SectionLabel>
            Configuring Stripe Locally &amp; on Vercel
          </SectionLabel>
          <SectionLabel>Adding a Stripe API Endpoint</SectionLabel>
          <SectionLabel>Connecting the UI to Stripe Checkout</SectionLabel>
          <SectionLabel>
            Saving Stripe Info to Associate With Users
          </SectionLabel>
          <SectionLabel>
            Restricting Access For Unpaid Users in the API
          </SectionLabel>
        </ChapterContents>
      </ComingSoon>
    </>
  );
}

TOC.getLayout = getLayout;

export default TOC;
